import { createFileRoute } from '@tanstack/react-router';
import { ArrowRight, Check, Minus, X } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';

export const Route = createFileRoute('/services')({
  component: ServicesPage,
});

const TIERS = [
  {
    name: 'Audit',
    bestFor: 'Existing POD brand',
    includes: ['2-week diagnostic', 'Operating gap analysis', 'Prioritized action plan'],
    pricing: 'Starting at $500',
    cta: 'Book an audit',
    intent: 'audit',
    featured: false,
  },
  {
    name: 'Operate',
    bestFor: 'Growing POD brand',
    includes: [
      'End-to-end managed operations',
      'Storefront migration / setup (in-house code or Shopify)',
      'Design pipeline + listing engine',
      'Fulfillment routing + CX',
      'Quarterly brand review',
    ],
    pricing: 'Custom pricing on request',
    cta: 'Start with an audit',
    intent: 'operations',
    featured: true,
  },
  {
    name: 'Build',
    bestFor: 'Net-new brand',
    includes: [
      'Brand + product definition',
      'Storefront build — AITOS in-house code or Shopify (your choice)',
      'Design + listing from scratch',
      'Multi-marketplace launch (Etsy, Amazon, eBay)',
      'Ongoing operations',
    ],
    pricing: 'Custom pricing on request',
    cta: 'Talk to us',
    intent: 'operations',
    featured: false,
  },
];

const PROCESS = [
  {
    n: '01',
    title: 'Discovery',
    text: 'Intro call to understand the brand, the operating problem, and the goal.',
  },
  {
    n: '02',
    title: 'Audit',
    text: '2-week diagnostic across design, listing, fulfillment, CX, and growth.',
  },
  { n: '03', title: 'Onboarding', text: 'Operating agreement, access handoff, SLAs, runbook.' },
  {
    n: '04',
    title: 'Operate',
    text: 'AITOS runs daily ops; weekly check-ins; monthly P&L review.',
  },
  {
    n: '05',
    title: 'Quarterly review',
    text: 'Brand strategy, new-category bets, ops improvements.',
  },
];

function ServicesPage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Managed POD Operations — AITOS Labs LLC</title>
        <meta
          name="description"
          content="Outsource your POD brand operations to AITOS Labs LLC. Audit, Operate, Build — three engagement tiers. The same stack we use on Elzary and Avyra Atlas."
        />
        <link rel="canonical" href="https://aitos.app/services" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'Managed POD Operations',
            provider: {
              '@type': 'Organization',
              name: 'AITOS Labs LLC',
              url: 'https://aitos.app',
            },
            description:
              'End-to-end Print-on-Demand brand operations: design, listing, fulfillment, customer experience, and growth.',
            areaServed: 'Worldwide',
          })}
        </script>
      </Helmet>

      <Navbar />

      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-purple-400 text-xs tracking-widest mb-4">MANAGED POD OPERATIONS</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Outsource your POD brand operations.
            <br />
            <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
              Keep the brand. Lose the ops headache.
            </span>
          </h1>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto">
            AITOS Labs LLC operates POD brands end-to-end — the same stack we use on Elzary and
            Avyra Atlas, available as a managed service.
          </p>
        </div>
      </section>

      {/* Tiers */}
      <section id="tiers" className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {TIERS.map((tier) => (
              <div
                key={tier.name}
                className={`relative rounded-2xl p-8 border transition-all duration-300 ${
                  tier.featured
                    ? 'bg-gradient-to-br from-purple-900/30 to-pink-900/20 border-purple-500/40 md:scale-105'
                    : 'bg-gray-900 border-gray-800 hover:border-purple-500/30'
                }`}
              >
                {tier.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-purple-500 text-white text-xs font-bold tracking-widest">
                    MOST POPULAR
                  </span>
                )}
                <h2 className="text-3xl font-bold mb-1">{tier.name}</h2>
                <p className="text-sm text-gray-400 mb-6">Best for: {tier.bestFor}</p>
                <ul className="space-y-2 mb-6">
                  {tier.includes.map((line) => (
                    <li key={line} className="flex items-start gap-2 text-sm text-gray-300">
                      <Check size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg font-bold mb-6 text-white">{tier.pricing}</p>
                <a
                  href={`/#cta?intent=${tier.intent}`}
                  className="inline-flex w-full justify-center items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:opacity-90 transition-opacity"
                >
                  {tier.cta}
                  <ArrowRight size={16} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Storefront stack choice */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-purple-400 text-xs tracking-widest mb-4">STOREFRONT STACK</p>
            <h2 className="text-4xl font-bold mb-3">You pick the stack. We operate it.</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Build and Operate engagements include storefront setup. Choose the option that fits
              your brand:
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/20 rounded-2xl p-8 border border-purple-500/30">
              <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs font-medium tracking-widest">
                AITOS FLAGSHIP STACK
              </span>
              <h3 className="text-2xl font-bold mt-4 mb-2">AITOS in-house storefront</h3>
              <p className="text-gray-300 mb-4">
                Same custom-built stack that powers <strong>elzary.com</strong> and{' '}
                <strong>avyraatlas.com</strong>. You own the code and the customer data; AITOS
                operates it for you.
              </p>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                  No platform lock-in, no monthly platform fees
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                  Tuned for AITOS operational tooling (design pipeline, listings, CX agent)
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                  Source code handed over at the end of engagement
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
              <span className="px-3 py-1 rounded-full bg-gray-700 text-gray-300 text-xs font-medium tracking-widest">
                ALTERNATIVE
              </span>
              <h3 className="text-2xl font-bold mt-4 mb-2">Shopify</h3>
              <p className="text-gray-300 mb-4">
                Prefer the Shopify ecosystem? We set up, theme, and integrate Shopify with the AITOS
                ops stack on top.
              </p>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                  Faster initial launch using Shopify themes + apps
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                  AITOS connects listings, fulfillment, and CX layers to Shopify
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-purple-400 flex-shrink-0 mt-0.5" />
                  You keep your Shopify account ownership
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-purple-400 text-xs tracking-widest mb-4">ENGAGEMENT PROCESS</p>
            <h2 className="text-4xl font-bold mb-3">From discovery to quarterly review.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {PROCESS.map((step) => (
              <div key={step.n} className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
                <p className="text-purple-400 text-xs font-bold mb-2">{step.n}</p>
                <h3 className="text-lg font-bold mb-1">{step.title}</h3>
                <p className="text-sm text-gray-400">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we don't do */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto bg-gray-900 rounded-2xl p-10 border border-gray-800">
          <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
            <Minus size={28} className="text-gray-500" />
            What we don't do
          </h2>
          <ul className="space-y-3 text-gray-300">
            <li className="flex items-start gap-3">
              <X size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
              <span>We don't sell SaaS access — we operate the brand for you.</span>
            </li>
            <li className="flex items-start gap-3">
              <X size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
              <span>
                We don't take on more than <strong className="text-white">3 new managed-service brands per quarter</strong>. Quality first; volume second.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <X size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
              <span>We don't take ownership of your brand IP — that stays with you.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-purple-900/30 to-pink-900/20 rounded-3xl p-10 md:p-14 border border-purple-500/30 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">Start with a 2-week brand audit.</h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            We diagnose your operations, then design an engagement scoped to your goals.
          </p>
          <a
            href="/#cta?intent=audit"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:opacity-90 transition-opacity"
          >
            Book a brand audit
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
